import { Box, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { integrateProjectWithFigmaFile, qk } from 'api'
import { DialogContent, ToastContent } from 'elements/feedback'
import { renderTextField } from 'elements/inputs'
import { useValidations } from 'lib/form-utils'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

const formId = 'CREATE_FIGMA_INTEGRATION_FORM'

type FormValues = {
  fileId: string
}

type CreateFigmaIntegrationFormProps = {
  projectId: string
  closeDialog: () => void
  authCode: string
}

export const CreateFigmaIntegrationForm = ({
  projectId,
  closeDialog,
  authCode,
}: CreateFigmaIntegrationFormProps) => {
  const { t } = useTranslation()

  const rules = useValidations()

  const queryClient = useQueryClient()

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      fileId: '',
    },
  })

  const $integrateProjectWithFigmaFile = useMutation(
    integrateProjectWithFigmaFile,
  )

  return (
    <DialogContent
      title={t('projects.settings.integrations.create_figma_integration')}
      isActionLoading={$integrateProjectWithFigmaFile.isLoading}
      onDeny={closeDialog}
      externalFormId={formId}
      confirmText={t('projects.settings.integrations.make_connection')}
    >
      <Box
        id={formId}
        component="form"
        onSubmit={handleSubmit(({ fileId }) =>
          $integrateProjectWithFigmaFile.mutate(
            {
              projectId,
              fileId,
              authCode,
            },
            {
              onSuccess: () => {
                toast.success(
                  <ToastContent
                    title={t(
                      'user_profile.integrations.figma_integration_successfully_created',
                    )}
                  />,
                )
                queryClient.invalidateQueries(
                  qk.projects.figmaFile.toKeyWithArgs({ projectId }),
                )
                closeDialog()
              },
            },
          ),
        )}
        mt={2}
        mb={1}
      >
        <Controller
          name="fileId"
          control={control}
          rules={rules.required}
          render={renderTextField({
            label: t('projects.settings.integrations.file_id'),
            placeholder: 'JCIMNIijyTe6FpWZabxDE9',
          })}
        />
      </Box>

      <Typography variant="caption2" color="text.secondary" display="block">
        <Trans
          i18nKey="projects.settings.integrations.figma_file_tip"
          components={{
            bold: <Typography variant="caption2" fontWeight={600} />,
          }}
        />
      </Typography>
    </DialogContent>
  )
}
